import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api.ts';
import { AdministrationRole, type TTaskApplication } from '~/modules/tasks/taskModels.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import type {
  TFetchTasksAdministrationTasksRequest,
  TFetchTasksSupervisorsRequest,
  TPatchTasksAssigneeRequest,
  TTasksAdministrationTasksResponse,
  TTasksSupervisors,
  TTasksUakdoAccess,
  TUpdateTasksStatusRequest,
} from '../types.ts';

export const fetchTasksAdministrationRoles = createAsyncThunk<AdministrationRole[]>(
  'tasks/fetchAdministrationRoles',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/guarantee-api/administrations/roles');

      return Array.isArray(data?.roles) ? (data.roles as AdministrationRole[]) : [];
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchTasksAdministrationTasks = createAsyncThunk<
  TTasksAdministrationTasksResponse,
  TFetchTasksAdministrationTasksRequest
>('tasks/fetchAdministrationTasks', async (input, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({
      page: String(input.page),
      size: String(input.size),
    });

    input.adminRoles.forEach((role) => params.append('adminRoles', role));
    input.sort?.forEach((value) => params.append('sort', value));

    const { data } = await api.get('/guarantee-api/administrations/tasks', { params });

    return {
      tasks: Array.isArray(data?.content) ? (data.content as TTaskApplication[]) : [],
      page: data?.number ?? input.page,
      size: data?.size ?? input.size,
      totalElements: data?.totalElements ?? 0,
    };
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchTasksUakdoAccess = createAsyncThunk<TTasksUakdoAccess>(
  'tasks/fetchUakdoAccess',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/guarantee-api/managers/uakdo/supervisor-info');

      return {
        hasResponsibleAccess: !!data?.hasResponsibleAccess,
        hasControllerAccess: !!data?.hasControllerAccess,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchTasksSupervisors = createAsyncThunk<
  TTasksSupervisors,
  TFetchTasksSupervisorsRequest
>('tasks/fetchSupervisors', async ({ taskId, unit }, { rejectWithValue }) => {
  try {
    const { data } = await api.get(
      `/guarantee-api/administrations/tasks/${encodeURIComponent(taskId)}/suppervisors`,
      { params: { unit } }
    );

    return {
      assignedEmployeeEmail: data?.assignedEmployeeEmail ?? null,
      supervisorEmails: Array.isArray(data?.supervisorEmails) ? data.supervisorEmails : [],
    };
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const patchTasksAssignee = createAsyncThunk<unknown, TPatchTasksAssigneeRequest>(
  'tasks/patchAssignee',
  async ({ applicationId, version, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(
        `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/assignee`,
        payload,
        { params: { version } }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

const createTasksApprovalStatusThunk = (resource: string) =>
  createAsyncThunk<unknown, TUpdateTasksStatusRequest>(
    `tasks/${resource}`,
    async ({ applicationId, version, status }, { rejectWithValue }) => {
      try {
        const { data } = await api.post(
          `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/${resource}`,
          null,
          { params: { applicationApprovalStatus: status, version } }
        );

        return data;
      } catch (error) {
        return thunkErrorHandler(error, rejectWithValue);
      }
    }
  );

export const updateTasksUdbtfStatus = createTasksApprovalStatusThunk('udbtf/approval-statuses');
export const updateTasksUoStatus = createTasksApprovalStatusThunk('uo/approval-statuses');
export const updateTasksUmvnStatus = createTasksApprovalStatusThunk('umvn/approval-statuses');

export const updateTasksApplicationStatus = createAsyncThunk<unknown, TUpdateTasksStatusRequest>(
  'tasks/updateApplicationStatus',
  async ({ applicationId, version, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/statuses`,
        null,
        { params: { applicationStatus: status, version } }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
